import { Currency, Dinero, dinero, toFormat, toUnit } from 'dinero.js'
import { EUR } from '@dinero.js/currencies'
import { toSnapshot } from '@dinero.js/core'
import {
    decimalToLocalizedString,
    formatNumberWithAbbreviation,
    getFormattedValueAndUnitFromNumber,
} from 'ComponentLibrary/src/common/numbers'
import { every, sumBy } from 'lodash'

export type MoneyPerAreaValue = {
    amount: number
    currency: string
    area_unit: 'm²'
}

export type MoneyPerUnitValue = {
    amount: number
    currency: string
    area_unit: 'EH'
}

export type MonetaryValueDataObjectType = {
    amount: number
    currency: string
}

export type ValueWithTrend<T extends number | MonetaryValueDataObjectType> = {
    value: T
    trend?: number
}

type CurrencyCode = keyof typeof currencySymbols

export class MonetaryValue {
    amount: number
    currency: string

    constructor(amount?: number, currency?: string) {
        this.amount = amount ?? 0
        this.currency = currency || 'EUR'
    }

    static fromDecimal(value = 0) {
        // Round to avoid decimal issues (amount is always an int)
        return new MonetaryValue(Math.round(value * 100))
    }

    static fromMonetaryValueDataObject(data: MonetaryValueDataObjectType | undefined): MonetaryValue {
        return new MonetaryValue(data?.amount, data?.currency)
    }

    static fromMoneyPerAreaValue(data: MoneyPerAreaValue | undefined): MonetaryValue {
        return new MonetaryValue(data?.amount, data?.currency)
    }

    static zero() {
        return new MonetaryValue()
    }

    static toAmount(value: number) {
        return value * 100
    }

    toDecimal() {
        return this.amount / 100
    }

    toMoneyPerAreaValue(): MoneyPerAreaValue {
        return { amount: this.amount, currency: this.currency, area_unit: 'm²' }
    }

    add(value: number): MonetaryValue {
        return new MonetaryValue(this.amount + value, this.currency)
    }

    subtract(value: number): MonetaryValue {
        return new MonetaryValue(this.amount - value, this.currency)
    }

    multiply(value: number): MonetaryValue {
        return new MonetaryValue(Math.round(this.amount * value), this.currency)
    }

    divide(value: number): MonetaryValue {
        return new MonetaryValue(Math.round(this.amount / value), this.currency)
    }

    valueOf() {
        return this.amount
    }

    currencySymbol() {
        return currencySymbols[this.currency as CurrencyCode]
    }

    format({
        unit,
        pretty = false,
        withoutDecimals = false,
        withoutCurrency = false,
        withoutSign = false,
        locale = 'de-DE',
    }: {
        unit?: string
        pretty?: boolean
        withoutDecimals?: boolean
        withoutCurrency?: boolean
        withoutSign?: boolean
        locale?: string
    } = {}): string {
        const currencySymbol = withoutCurrency ? '' : this.currencySymbol()
        const unitString = unit ? `/${unit}` : ''
        const monetaryValue = withoutSign ? new MonetaryValue(Math.abs(this.amount), this.currency) : this
        const displayAmount = pretty
            ? formatNumberWithAbbreviation(monetaryValue.toDecimal(), 2, false, undefined, locale)
            : withoutDecimals
              ? decimalToLocalizedString(monetaryValue.toDecimal(), true, 0, undefined, locale)
              : monetaryToLocalizedString(monetaryValue, false, false, locale)
        const formattedValue = `${displayAmount} ${currencySymbol}${unitString}`.trim()
        return formattedValue || '-'
    }
}

export function getFormattedValueAndUnitFromMonetaryValue(monetaryValue?: MonetaryValueDataObjectType, locale = 'de-DE') {
    if (!monetaryValue) {
        return { value: '-' }
    }
    const monetaryValueClassObject = MonetaryValue.fromMonetaryValueDataObject(monetaryValue)
    return getFormattedValueAndUnitFromNumber(
        monetaryValueClassObject.toDecimal(),
        monetaryValueClassObject.currencySymbol(),
        undefined,
        locale
    )
}

export function getFormattedValueAndUnitFromMoneyPerAreaValue(moneyPerArea?: MoneyPerAreaValue, locale = 'de-DE') {
    if (!moneyPerArea) {
        return { value: '-' }
    }
    const monetaryValueClassObject = MonetaryValue.fromMonetaryValueDataObject(moneyPerArea)
    return getFormattedValueAndUnitFromNumber(
        monetaryValueClassObject.toDecimal(),
        `${monetaryValueClassObject.currencySymbol()}/m²`,
        undefined,
        locale
    )
}

export function getFormattedValueAndUnitFromMoneyPerUnitValue(moneyPerUnit?: MoneyPerUnitValue, locale = 'de-DE') {
    if (!moneyPerUnit) {
        return { value: '-' }
    }
    const monetaryValueClassObject = MonetaryValue.fromMonetaryValueDataObject(moneyPerUnit)
    return getFormattedValueAndUnitFromNumber(
        monetaryValueClassObject.toDecimal(),
        `${monetaryValueClassObject.currencySymbol()}/mEH`,
        undefined,
        locale
    )
}

// Currency code is in ISO 4217 format
const currencySymbols = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    // Add more currency symbols as needed
}

export const isMonetaryValueDataObject = (value: MonetaryValue | unknown): value is MonetaryValueDataObjectType => {
    if ((value as MonetaryValue)?.currency === undefined || (value as MonetaryValue)?.amount === undefined) {
        return false
    }
    return Number.isInteger((value as MonetaryValue)?.amount)
}

export const isMonetaryValue = (value: MonetaryValue | unknown, allowNaNAmount = false): value is MonetaryValue => {
    if (!(value instanceof MonetaryValue)) {
        return false
    }
    if (allowNaNAmount && isNaN(value?.amount)) {
        return true
    }
    return Number.isInteger(value.amount)
}

export function isMonetaryValueOrNull(value: unknown): value is MonetaryValue | null {
    return value === null || isMonetaryValueDataObject(value)
}

export const toEuroString = (dineroObject: Dinero<number> | null, showCurrency?: boolean, locale = 'de-DE') => {
    if (dineroObject === null) {
        return null
    }
    function transformer({ amount }: { amount: number; currency: Currency<number> }) {
        return amount.toLocaleString(locale, {
            style: showCurrency ? 'currency' : 'decimal',
            minimumFractionDigits: showCurrency ? undefined : 2,
            maximumFractionDigits: showCurrency ? undefined : 2,
            currency: 'EUR',
        })
    }

    return toFormat(dineroObject, transformer)
}

// Rounded to the nearest euro
export const toRoundedEuroString = (dineroObject: Dinero<number>, showCurrency?: boolean, locale = 'de-DE') => {
    function transformer({ amount }: { amount: number; currency: Currency<number> }) {
        return amount.toLocaleString(locale, {
            style: showCurrency ? 'currency' : 'decimal',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
            currency: 'EUR',
        })
    }

    return toFormat(dineroObject, transformer)
}

export const euro = (monetaryValue: number): Dinero<number> => {
    return dinero({ amount: monetaryValue, currency: EUR })
}

export const dineroToDecimal = (dineroObject?: Dinero<number> | null): number | null => {
    if (!dineroObject) {
        return null
    }
    return toUnit(dineroObject)
}

export const decimalToEuro = (decimal: number): Dinero<number> => {
    return euro(Math.round(decimal * 100))
}

export const germanStringToNumber = (germanNumber: string): number => {
    const trimmedInput = germanNumber.trim()
    if (trimmedInput === '') {
        return 0
    }

    // Remove every character except digits, decimal separators, and minus signs so formatted user input can be parsed.
    const sanitizedNumber = germanNumber.replace(/[^\d.,-]/g, '')

    // If we did not keep at least one digit, the input cannot be parsed into a number.
    if (!/\d/.test(sanitizedNumber)) {
        return Number.NaN
    }

    return Number(sanitizedNumber.replaceAll('.', '').replace(',', '.'))
}

export const toMonetaryJSON = (dineroObject: Dinero<number>): MonetaryValue => {
    const moneySnapshot = toSnapshot(dineroObject)
    return new MonetaryValue(moneySnapshot.amount, moneySnapshot.currency.code as CurrencyCode)
}

export const fromMonetaryValue = (monetaryValue?: MonetaryValue | MonetaryValueDataObjectType | null) => {
    if (!monetaryValue) {
        return null
    }
    return euro(monetaryValue.amount)
}

export const addMonetaryValues = (
    monetaryValue1: MonetaryValueDataObjectType,
    monetaryValue2?: MonetaryValueDataObjectType
): MonetaryValue => {
    if (!monetaryValue2) {
        return new MonetaryValue(monetaryValue1.amount, monetaryValue1.currency)
    }
    if (monetaryValue1.currency !== monetaryValue2.currency) {
        throw new CurrencyMismatchException()
    }
    return new MonetaryValue(monetaryValue1.amount + monetaryValue2.amount, monetaryValue1.currency)
}

export const multiplyMonetaryValue = (monetaryValue?: MonetaryValue | MonetaryValueDataObjectType, number?: number) => {
    if (!monetaryValue || number === undefined) {
        return null
    }
    return new MonetaryValue(Math.round(monetaryValue.amount * number), monetaryValue.currency)
}

export const sumMonetaryValues = (values: MonetaryValue[]) => {
    if (!every(values, (value) => value.currency === values[0].currency)) {
        throw new CurrencyMismatchException()
    }

    return new MonetaryValue(sumBy(values, (value) => value.amount))
}

export const subtractMonetaryValues = (
    monetaryValue1: MonetaryValueDataObjectType,
    monetaryValue2?: MonetaryValueDataObjectType
): MonetaryValue => {
    if (!monetaryValue2) {
        return new MonetaryValue(monetaryValue1.amount, monetaryValue1.currency)
    }
    if (monetaryValue1.currency !== monetaryValue2.currency) {
        throw new CurrencyMismatchException()
    }
    return new MonetaryValue(monetaryValue1.amount - monetaryValue2.amount, monetaryValue1.currency)
}

class CurrencyMismatchException extends Error {
    constructor() {
        super('Cannot add values with mismatching currencies')
        this.name = 'CurrencyMismatchException'
    }
}

export const monetaryToLocalizedString = (
    monetaryValue?: MonetaryValue | MonetaryValueDataObjectType | null,
    showCurrency?: boolean,
    rounded?: boolean,
    locale = 'de-DE'
): string | null => {
    const dineroObject = fromMonetaryValue(monetaryValue)
    if (!monetaryValue || dineroObject === null) {
        return null
    }
    if (rounded) {
        return toRoundedEuroString(dineroObject, showCurrency, locale)
    }
    return toEuroString(dineroObject, showCurrency, locale)
}

export const monetaryToGermanString = (
    monetaryValue?: MonetaryValue | MonetaryValueDataObjectType | null,
    showCurrency?: boolean,
    rounded?: boolean
): string | null => monetaryToLocalizedString(monetaryValue, showCurrency, rounded, 'de-DE')

export const monetaryToDecimal = (monetaryValue?: MonetaryValueDataObjectType | undefined): number | undefined => {
    if (!monetaryValue) {
        return undefined
    }

    return MonetaryValue.fromMonetaryValueDataObject(monetaryValue).toDecimal()
}
